import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  Play,
  Square,
  Pause,
  RotateCcw,
  Power,
  Trash2,
  Settings,
  Plus,
  Cpu,
  Activity,
  AlertCircle,
  Clock,
  MessageSquare,
  Rocket,
  Archive,
  Copy,
  HeartPulse,
  ArrowLeftRight,
} from 'lucide-react';
import apiClient from '../api/client';
import { getAgentDoctor, DOCTOR_GRADE_LABELS } from '../api/doctor';
import type { DoctorReport } from '../api/doctor';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import AgentTransferDialog from '../components/AgentTransferDialog';

interface Agent {
  id: string;
  name: string;
  description: string | null;
  domain: string | null;
  status: string;
  framework: string | null;
  runtime_count: number;
  uptime_seconds: number;
  task_count: number;
  created_at: string;
  /** 写权限（dashboard 按 check_agent_permission 口径下发；旧响应缺省视为可写） */
  can_write?: boolean;
  runtimes?: RuntimeView[];
}

interface RuntimeView {
  id: string;
  agent_id: string;
  agent_name?: string;
  agent_description?: string | null;
  framework: string;
  status: string;
  container_id: string | null;
  port: number | null;
  endpoint_url: string | null;
  error_message: string | null;
  runtime_info?: { stub?: boolean; fallback_reason?: string } | null;
  deployed_at: string | null;
  last_heartbeat: string | null;
}

interface DashboardData {
  agents: Agent[];
  total_agents: number;
  status_counts: Record<string, number>;
  total_runtimes: number;
  running_runtimes: number;
  stopped_runtimes: number;
  error_runtimes: number;
}

const FRAMEWORK_MAP: Record<string, { label: string; class: string }> = {
  celestial_toad: { label: 'CT v4', class: 'ct' },
  hermes: { label: 'Hermes', class: 'hermes' },
  openclaw: { label: 'OpenClaw', class: 'openclaw' },
  openhuman: { label: 'OpenHuman', class: 'openhuman' },
  generic: { label: 'Generic', class: 'generic' },
};

const DOCTOR_BADGE_CLASS: Record<string, string> = {
  healthy: 'success',
  attention: 'warning',
  critical: 'error',
};

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

export default function AgentDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showErrorPanel, setShowErrorPanel] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  // Doctor 健康分：按 agentId 缓存；接口失败的 Agent 不展示徽章（诚实降级）
  const [doctorReports, setDoctorReports] = useState<Record<string, DoctorReport>>({});
  // 归属转移弹窗目标 Agent（null = 关闭）
  const [transferTarget, setTransferTarget] = useState<{ id: string; name: string } | null>(null);
  // 租户上下文统一由顶栏切换器 + client.ts 请求头注入决定，本页不做空间选择
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const errorRuntimes = useMemo(() => {
    const out: RuntimeView[] = [];
    data?.agents.forEach((agent) => {
      (agent.runtimes ?? []).forEach((rt) => {
        if (rt.status === 'error') {
          out.push({ ...rt, agent_name: agent.name, agent_description: agent.description });
        }
      });
    });
    return out;
  }, [data]);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await apiClient.get('/dashboard/agents');
      setData(res.data);
      // 异步补齐各 Agent 的健康分徽章；单个失败不影响其他
      const agents: Agent[] = res.data?.agents ?? [];
      const results = await Promise.allSettled(agents.map((a) => getAgentDoctor(a.id)));
      setDoctorReports(() => {
        const next: Record<string, DoctorReport> = {};
        results.forEach((r) => {
          if (r.status === 'fulfilled') next[r.value.agent_id] = r.value;
        });
        return next;
      });
    } catch (e) {
      toast.error('加载 Agent 数据失败: ' + getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    // 立即刷新一次，然后每 5s 轮询；通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadDashboard();
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const doAction = async (agentId: string, action: string, confirmOpts?: { title: string; message?: string }) => {
    if (confirmOpts && !(await confirm(confirmOpts))) return;
    setActionLoading((prev) => ({ ...prev, [agentId + action]: true }));
    try {
      await apiClient.post(`/agents/${agentId}/runtime/${action}`);
      toast.success(`操作成功: ${action}`);
      loadDashboard();
    } catch (e) {
      toast.error('操作失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading((prev) => ({ ...prev, [agentId + action]: false }));
    }
  };

  const deleteAgent = async (agentId: string, agentName: string) => {
    if (
      !(await confirm({
        title: '确定要删除这个 Agent 吗？',
        message: '此操作不可撤销。',
        danger: true,
        requireText: agentName,
      }))
    )
      return;
    setActionLoading((prev) => ({ ...prev, [agentId + 'delete']: true }));
    try {
      await apiClient.delete(`/agents/${agentId}`);
      toast.success('Agent 已删除');
      loadDashboard();
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading((prev) => ({ ...prev, [agentId + 'delete']: false }));
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const stats = data || {
    agents: [],
    total_agents: 0,
    status_counts: {},
    total_runtimes: 0,
    running_runtimes: 0,
    stopped_runtimes: 0,
    error_runtimes: 0,
  };

  return (
    <div>
      {/* 统计卡片行 */}
      <div className="grid-cols-4" style={{ display: 'grid', gap: '20px', marginBottom: '28px' }}>
        <div className="stat-card">
          <div className="stat-label">Agent 总数</div>
          <div className="stat-value">{stats.total_agents}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            共 {stats.total_runtimes} 运行时
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-label">
            <Activity size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            运行中
          </div>
          <div className="stat-value">{stats.running_runtimes}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            {stats.status_counts['running'] || 0} 个活跃 Agent
          </div>
        </div>
        <div className="stat-card" style={{ borderTop: '2px solid var(--text-muted)' }}>
          <div className="stat-label">
            <Square size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            已停止
          </div>
          <div className="stat-value" style={{ background: 'linear-gradient(135deg, var(--text-secondary), var(--text-muted))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {stats.stopped_runtimes}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            等待重启
          </div>
        </div>
        <div
          className={`stat-card error ${stats.error_runtimes === 0 ? 'opacity-50' : ''}`}
          style={{ width: '100%', cursor: stats.error_runtimes > 0 ? 'pointer' : 'default' }}
          onClick={() => stats.error_runtimes > 0 && setShowErrorPanel(true)}
          role={stats.error_runtimes > 0 ? 'button' : undefined}
          tabIndex={stats.error_runtimes > 0 ? 0 : undefined}
        >
          <div className="stat-label">
            <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            异常
          </div>
          <div className="stat-value">{stats.error_runtimes}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            需要排查 {stats.error_runtimes > 0 ? '（点击查看）' : ''}
          </div>
        </div>
      </div>

      {/* 头部操作栏 */}
      <div className="flex items-center justify-between" style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
          <Cpu size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Agent 列表
        </h2>
        <div className="flex items-center" style={{ gap: '12px' }}>
          <button className="primary glow" onClick={() => navigate('/agents/create')}>
            <Plus size={16} /> 新建 Agent
          </button>
        </div>
      </div>

      {/* Agent 卡片网格 */}
      {stats.agents.length === 0 ? (
        <div className="empty-tech">
          <div className="empty-icon">
            <Bot size={40} />
          </div>
          <h3>暂无 Agent</h3>
          <p>点击「新建 Agent」开始创建你的第一个 AI Agent</p>
          <button className="primary glow" onClick={() => navigate('/agents/create')}>
            <Plus size={16} /> 新建 Agent
          </button>
        </div>
      ) : (
        <div className="agent-card-grid">
          {stats.agents.map((agent) => {
            const fw = agent.framework ? FRAMEWORK_MAP[agent.framework] : null;
            const isLoading = (key: string) => actionLoading[agent.id + key];

            return (
              <div key={agent.id} className="agent-card animate-fade-in">
                <div className="card-header">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.name}</div>
                    <div className="card-meta">
                      {agent.domain || '未指定领域'} · {agent.runtime_count} 个运行时
                    </div>
                  </div>
                  <div className="flex items-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
                    <span className={`pulse-dot ${agent.status}`} />
                    {fw && (
                      <span className={`framework-badge ${fw.class}`}>{fw.label}</span>
                    )}
                    {doctorReports[agent.id] && (
                      <span
                        className={`badge ${DOCTOR_BADGE_CLASS[doctorReports[agent.id].grade] || 'info'}`}
                        title={`体检：${DOCTOR_GRADE_LABELS[doctorReports[agent.id].grade]}，${doctorReports[agent.id].issues.length} 个待处理项`}
                      >
                        <HeartPulse size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                        {doctorReports[agent.id].score}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex" style={{ gap: '16px', marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <div className="flex items-center" style={{ gap: '4px' }}>
                    <Clock size={14} />
                    <span>{agent.uptime_seconds > 0 ? formatUptime(agent.uptime_seconds) : '—'}</span>
                  </div>
                  <div className="flex items-center" style={{ gap: '4px' }}>
                    <MessageSquare size={14} />
                    <span>{agent.task_count} 任务</span>
                  </div>
                </div>

                <div className="card-actions">
                  {agent.status === 'created' && (
                    <>
                      <button
                        className="primary"
                        onClick={() => navigate(`/agents/${agent.id}/deployments`)}
                        title="去部署"
                      >
                        <Rocket size={14} style={{ marginRight: 4 }} /> 部署
                      </button>
                      <button
                        className="secondary"
                        onClick={() => doAction(agent.id, 'start')}
                        disabled={isLoading('start')}
                        title="启动"
                      >
                        <Play size={14} />
                      </button>
                    </>
                  )}
                  {(agent.status === 'pending' || agent.status === 'deploying') && (
                    <span className="text-muted" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="spinner" style={{ width: 14, height: 14 }} />
                      部署中
                    </span>
                  )}
                  {agent.status === 'running' && (
                    <>
                      <button
                        className="secondary"
                        onClick={() => doAction(agent.id, 'stop')}
                        disabled={isLoading('stop')}
                        title="停止"
                      >
                        <Square size={14} />
                      </button>
                      <button
                        className="secondary"
                        onClick={() => doAction(agent.id, 'pause')}
                        disabled={isLoading('pause')}
                        title="暂停"
                      >
                        <Pause size={14} />
                      </button>
                      <button
                        className="secondary"
                        onClick={() => doAction(agent.id, 'restart')}
                        disabled={isLoading('restart')}
                        title="重启"
                      >
                        <RotateCcw size={14} />
                      </button>
                    </>
                  )}
                  {agent.status === 'stopped' && (
                    <button
                      className="secondary"
                      onClick={() => doAction(agent.id, 'start')}
                      disabled={isLoading('start')}
                      title="启动"
                    >
                      <Play size={14} />
                    </button>
                  )}
                  {agent.status === 'paused' && (
                    <button
                      className="secondary"
                      onClick={() => doAction(agent.id, 'unpause')}
                      disabled={isLoading('unpause')}
                      title="恢复"
                    >
                      <Play size={14} />
                    </button>
                  )}
                  {agent.status === 'error' && (
                    <button
                      className="secondary"
                      onClick={() => doAction(agent.id, 'restart')}
                      disabled={isLoading('restart')}
                      title="重启"
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                  {(agent.status === 'running' || agent.status === 'stopped' || agent.status === 'error' || agent.status === 'paused') && (
                    <button
                      className="secondary"
                      onClick={() => doAction(agent.id, 'shutdown', { title: '确定要下线这个 Agent 吗？', message: '将释放所有资源。' })}
                      disabled={isLoading('shutdown')}
                      title="下线"
                    >
                      <Power size={14} />
                    </button>
                  )}
                  {agent.status === 'offline' && (
                    <button
                      className="secondary"
                      onClick={() => navigate('/archives')}
                      title="已下线，可前往归档中心恢复"
                    >
                      <Archive size={14} />
                    </button>
                  )}
                  <button
                    className="secondary"
                    onClick={() => navigate(`/agents/${agent.id}/config-center`)}
                    title="配置"
                  >
                    <Settings size={14} />
                  </button>
                  {agent.can_write !== false && (
                    <button
                      className="secondary"
                      onClick={() => setTransferTarget({ id: agent.id, name: agent.name })}
                      title="转移归属"
                    >
                      <ArrowLeftRight size={14} />
                    </button>
                  )}
                  <button
                    className="secondary"
                    onClick={() => deleteAgent(agent.id, agent.name)}
                    disabled={isLoading('delete')}
                    title="删除"
                    style={{ color: 'var(--color-error)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 异常详情抽屉 */}
      {showErrorPanel && (
        <div
          className="drawer-backdrop"
          onClick={() => setShowErrorPanel(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 50,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            className="drawer"
            style={{
              width: 420,
              maxWidth: '90vw',
              height: '100%',
              background: 'var(--bg-primary)',
              borderLeft: '1px solid var(--border-default)',
              padding: 24,
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>异常运行时</h3>
              <button className="secondary" onClick={() => setShowErrorPanel(false)}>关闭</button>
            </div>
            {errorRuntimes.length === 0 ? (
              <p className="text-muted">暂无异常</p>
            ) : (
              <div className="flex flex-col gap-3">
                {errorRuntimes.map((rt) => (
                  <div
                    key={rt.id}
                    className="card"
                    style={{ borderLeft: '3px solid var(--color-error)' }}
                  >
                    <div className="card-title">{rt.agent_name || '未命名 Agent'}</div>
                    <div className="card-meta" style={{ marginTop: 4 }}>
                      {rt.agent_description || '暂无描述'}
                    </div>
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                      <span className="text-muted">框架：</span>{FRAMEWORK_MAP[rt.framework]?.label || rt.framework}
                    </div>
                    {rt.error_message && (
                      <div
                        className="text-muted"
                        style={{
                          marginTop: 12,
                          padding: 12,
                          background: 'rgba(248,81,73,0.06)',
                          borderRadius: 8,
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {rt.error_message}
                      </div>
                    )}
                    <div className="flex gap-2" style={{ marginTop: 12 }}>
                      <button
                        className="secondary"
                        onClick={() => navigate(`/agents/${rt.agent_id}/config-center`)}
                      >
                        前往配置
                      </button>
                      <button
                        className="secondary"
                        onClick={() => doAction(rt.agent_id, 'restart')}
                      >
                        重启
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 运行时 / 容器视图 */}
      <div className="flex items-center justify-between" style={{ marginTop: '32px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
          <Cpu size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          运行时 / 容器视图
        </h2>
      </div>
      {(() => {
        const runtimes: RuntimeView[] = [];
        const agentMap = new Map(stats.agents.map((a) => [a.id, a.name]));
        stats.agents.forEach((agent) => {
          (agent.runtimes ?? []).forEach((rt) => {
            runtimes.push({ ...rt, agent_name: agentMap.get(rt.agent_id) });
          });
        });
        if (runtimes.length === 0) {
          return <p className="text-muted">暂无运行时</p>;
        }
        return (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>框架</th>
                  <th>状态</th>
                  <th>容器 ID</th>
                  <th>端口</th>
                  <th>端点</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {runtimes.map((rt) => (
                  <tr key={rt.id}>
                    <td>{rt.agent_name || rt.agent_id?.slice(0, 8) || '—'}</td>
                    <td>{FRAMEWORK_MAP[rt.framework]?.label || rt.framework}</td>
                    <td>
                      <span className={`badge ${rt.status === 'running' ? 'success' : rt.status === 'error' ? 'error' : 'info'}`}>{rt.status}</span>
                      {rt.runtime_info?.stub && (
                        <span className="badge warning" style={{ marginLeft: 4 }} title={rt.runtime_info?.fallback_reason || 'Docker 启动失败，当前为 stub 假运行'}>stub</span>
                      )}
                    </td>
                    <td>
                      <code style={{ fontSize: 12 }}>
                        {rt.container_id ? `${rt.container_id.slice(0, 16)}...` : '—'}
                      </code>
                    </td>
                    <td>{rt.port ?? '—'}</td>
                    <td>
                      {rt.endpoint_url ? (
                        <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{rt.endpoint_url}</code>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {rt.endpoint_url && (
                          <button
                            className="secondary"
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={() => {
                              const textarea = document.createElement('textarea');
                              textarea.value = rt.endpoint_url!;
                              textarea.style.position = 'fixed';
                              textarea.style.left = '-9999px';
                              document.body.appendChild(textarea);
                              textarea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textarea);
                              toast.success('端点已复制');
                            }}
                            title="复制端点"
                          >
                            <Copy size={14} />
                          </button>
                        )}
                        {rt.container_id && (
                          <button
                            className="secondary"
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={() => {
                              const textarea = document.createElement('textarea');
                              textarea.value = rt.container_id!;
                              textarea.style.position = 'fixed';
                              textarea.style.left = '-9999px';
                              document.body.appendChild(textarea);
                              textarea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textarea);
                              toast.success('容器 ID 已复制');
                            }}
                            title="复制容器 ID"
                          >
                            容器 ID
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
      {dialog}
      {transferTarget && (
        <AgentTransferDialog
          agentId={transferTarget.id}
          agentName={transferTarget.name}
          onTransferred={() => void loadDashboard()}
          onClose={() => setTransferTarget(null)}
        />
      )}
    </div>
  );
}
